import { useState, useEffect, useCallback, useRef } from 'react';
import apiConfig from '../apiConfig';

type NetworkStatus = 'online' | 'offline' | 'unstable';

const HEALTH_CHECK_INTERVAL = 30_000;
const HEALTH_CHECK_TIMEOUT = 10_000;
const UNSTABLE_THRESHOLD = 2;

export const useNetworkStatus = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>(
    navigator.onLine ? 'online' : 'offline',
  );
  const failCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const checkConnection = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

    try {
      const response = await fetch(`${apiConfig.baseURL}/health`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Health check failed with status ${response.status}`);
      }

      failCountRef.current = 0;
      setStatus('online');
    } catch {
      failCountRef.current += 1;
      setStatus(
        failCountRef.current >= UNSTABLE_THRESHOLD ? 'offline' : 'unstable',
      );
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      checkConnection();
    };
    const handleOffline = () => {
      failCountRef.current = 0;
      setStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    checkConnection();
    timerRef.current = setInterval(checkConnection, HEALTH_CHECK_INTERVAL);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(timerRef.current);
    };
  }, [checkConnection]);

  return status;
};
